import { BufferGeometry } from 'three/src/core/BufferGeometry';

export interface HorizontalPoint {
  x: number;
  z: number;
}

export interface OrientedFootprint {
  /** Extent along strike — the longer side of the tightest horizontal rectangle. */
  length: number;
  /** Extent across strike — the shorter side. */
  width: number;
  /** Unit vector along strike, on the horizontal plane. */
  lengthAxis: HorizontalPoint;
  /** Unit vector across strike: lengthAxis rotated a quarter turn about +Y. */
  widthAxis: HorizontalPoint;
  /** Centre of the rectangle on the horizontal plane. */
  centreX: number;
  centreZ: number;
}

const cross = (o: HorizontalPoint, a: HorizontalPoint, b: HorizontalPoint): number => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);

// Andrew's monotone chain, returned counter-clockwise without a repeated closing point. The reduce
// pushes into its own accumulator rather than spreading a new array per point: a mine solid carries
// six figures of vertices and the spread form is quadratic.
export const convexHullXZ = (points: HorizontalPoint[]): HorizontalPoint[] => {
  if (points.length < 3) return [...points];

  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.z - b.z : a.x - b.x));
  const chain = (input: HorizontalPoint[]): HorizontalPoint[] => input.reduce<HorizontalPoint[]>((acc, point) => {
    while (acc.length >= 2 && cross(acc[acc.length - 2], acc[acc.length - 1], point) <= 0) acc.pop();
    acc.push(point);
    return acc;
  }, []);

  const lower = chain(sorted);
  const upper = chain([...sorted].reverse());

  // Each chain repeats the other's endpoints, so drop the last of both.
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

const axisAlignedFootprint = (points: HorizontalPoint[]): OrientedFootprint => {
  const xs = points.map((point) => point.x);
  const zs = points.map((point) => point.z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  const spanX = maxX - minX;
  const spanZ = maxZ - minZ;
  const alongX = spanX >= spanZ;

  return {
    length: alongX ? spanX : spanZ,
    width: alongX ? spanZ : spanX,
    lengthAxis: alongX ? { x: 1, z: 0 } : { x: 0, z: 1 },
    widthAxis: alongX ? { x: 0, z: 1 } : { x: -1, z: 0 },
    centreX: (minX + maxX) / 2,
    centreZ: (minZ + maxZ) / 2,
  };
};

interface Candidate {
  area: number;
  along: number;
  across: number;
  axisX: number;
  axisZ: number;
  centreX: number;
  centreZ: number;
}

// The minimum-area enclosing rectangle, by the rotating-calipers theorem that one of its sides must
// lie flush with a hull edge — so testing every edge finds it exactly.
//
// A principal-component fit would be the shorter code, but it weights by vertex density, and mine
// solids are tessellated unevenly (fine around detailed lobes, coarse across flat walls). That makes
// PCA track the meshing rather than the shape. The hull depends only on the outline.
const minimumAreaRectangle = (hull: HorizontalPoint[]): Candidate | null => hull.reduce<Candidate | null>(
  (best, point, index) => {
    const next = hull[(index + 1) % hull.length];
    const edgeX = next.x - point.x;
    const edgeZ = next.z - point.z;
    const edgeLength = Math.hypot(edgeX, edgeZ);
    if (edgeLength === 0) return best;

    const axisX = edgeX / edgeLength;
    const axisZ = edgeZ / edgeLength;

    // Every hull point in the edge's frame: `a` along the edge, `b` along its normal (-axisZ, axisX).
    const extent = hull.reduce(
      (acc, vertex) => {
        const a = vertex.x * axisX + vertex.z * axisZ;
        const b = -vertex.x * axisZ + vertex.z * axisX;
        return {
          minA: Math.min(acc.minA, a),
          maxA: Math.max(acc.maxA, a),
          minB: Math.min(acc.minB, b),
          maxB: Math.max(acc.maxB, b),
        };
      },
      { minA: Infinity, maxA: -Infinity, minB: Infinity, maxB: -Infinity },
    );

    const along = extent.maxA - extent.minA;
    const across = extent.maxB - extent.minB;
    const area = along * across;
    if (best !== null && area >= best.area) return best;

    const midA = (extent.minA + extent.maxA) / 2;
    const midB = (extent.minB + extent.maxB) / 2;

    return {
      area,
      along,
      across,
      axisX,
      axisZ,
      // Rotate the centre back out of the edge frame.
      centreX: midA * axisX - midB * axisZ,
      centreZ: midA * axisZ + midB * axisX,
    };
  },
  null,
);

/**
 * The tightest horizontal rectangle enclosing a set of points, with its longer side taken as strike.
 *
 * A world-axis bounding box measures a diagonally-lying stope's own diagonal in both directions,
 * reporting it as far wider than it is. This measures the shape instead of the grid.
 */
export const orientedFootprintOfPoints = (points: HorizontalPoint[]): OrientedFootprint | null => {
  if (points.length === 0) return null;

  const hull = convexHullXZ(points);
  // Fewer than three hull points means the footprint is a point or a line — no rectangle to orient.
  if (hull.length < 3) return axisAlignedFootprint(points);

  const best = minimumAreaRectangle(hull);
  if (best === null) return axisAlignedFootprint(points);

  const alongIsLonger = best.along >= best.across;
  const lengthAxis = alongIsLonger
    ? { x: best.axisX, z: best.axisZ }
    : { x: -best.axisZ, z: best.axisX };

  return {
    length: alongIsLonger ? best.along : best.across,
    width: alongIsLonger ? best.across : best.along,
    lengthAxis,
    widthAxis: { x: -lengthAxis.z, z: lengthAxis.x },
    centreX: best.centreX,
    centreZ: best.centreZ,
  };
};

/** As above, reading the horizontal projection of a geometry's vertices. */
export const orientedFootprint = (geometry: BufferGeometry): OrientedFootprint | null => {
  const position = geometry.getAttribute('position');
  if (!position) return null;

  const points = Array.from({ length: position.count }, (_unused, index) => ({
    x: position.getX(index),
    z: position.getZ(index),
  }));

  return orientedFootprintOfPoints(points);
};
